// @ts-nocheck
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, Download, Eye, FileText, FilePlus, Loader, RefreshCw } from 'lucide-react';
import {
  Badge,
  InfoBanner,
  workflowTableHeadClass,
  workflowTableIconButtonEditClass,
  workflowTableIconButtonViewClass,
  workflowToolbarButtonClass,
  workflowToolbarPrimaryButtonClass,
} from '../../components/WorkflowFormPrimitives';
import { ErrorDetailModal } from '../../components/ErrorDetailModal';
import { TruncatedErrorCell } from '../../components/TruncatedErrorCell';
import {
  AnalysisDocumentRow,
  DOCUMENT_KINDS,
  downloadAnalysisDocument,
  generateAllAnalysisDocuments,
  generateAnalysisDocument,
  getAnalysisDocuments,
  getLastAnalysisRun,
} from '../../../../services/AnalysisApiClient';
import { downloadAuditExport } from '../../../../services/ApprovalEnvelopeApiClient';
import { formatIsoDateTime } from '../../../../utils/formatIsoDateTime';

interface DocumentsTableProps {
  analysisId: string | null;
  /** When set, scopes the listing to that run. Omit to show the most recent versions across runs. */
  runId?: string | null;
  /** Disables generate buttons (e.g. when the analysis is locked or the run is stale). */
  generationDisabled?: boolean;
  /** Tooltip / message shown on disabled rows. */
  generationDisabledReason?: string;
  /** Hook for parent to react to a preview request (open a modal, etc.). */
  onPreview?: (doc: AnalysisDocumentRow) => void;
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function statusBadgeVariant(status: string): 'success' | 'warning' | 'danger' | 'default' {
  if (status === 'Ready') return 'success';
  if (status === 'Generating') return 'warning';
  if (status === 'Failed') return 'danger';
  return 'default';
}

export function DocumentsTable({
  analysisId,
  runId,
  generationDisabled,
  generationDisabledReason,
  onPreview,
}: DocumentsTableProps) {
  const [rows, setRows] = useState<AnalysisDocumentRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [resolvedRunId, setResolvedRunId] = useState<string | null>(runId ?? null);
  const [errorDetail, setErrorDetail] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (runId) {
      setResolvedRunId(runId);
      return () => {
        cancelled = true;
      };
    }
    if (!analysisId) {
      setResolvedRunId(null);
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      try {
        const last = await getLastAnalysisRun(analysisId);
        if (!cancelled) {
          setResolvedRunId(last?.runId ?? null);
        }
      } catch {
        if (!cancelled) setResolvedRunId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [analysisId, runId]);

  const refresh = useCallback(async () => {
    if (!analysisId) {
      setRows([]);
      return;
    }
    try {
      const data = await getAnalysisDocuments(analysisId, {
        runId: resolvedRunId ?? undefined,
        latestOnly: true,
      });
      setRows(data);
      setLoadError(null);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load documents.';
      setLoadError(message);
      setRows([]);
    }
  }, [analysisId, resolvedRunId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleDownload = async (doc: AnalysisDocumentRow) => {
    setBusy((prev) => new Set(prev).add(doc.id));
    setActionError(null);
    try {
      await downloadAnalysisDocument(doc);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Download failed.');
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  };

  const handleRegenerate = async (doc: AnalysisDocumentRow) => {
    setBusy((prev) => new Set(prev).add(doc.id));
    setActionError(null);
    try {
      await generateAnalysisDocument(doc.analysisId, doc.analysisRunId, {
        kind: doc.kind,
        format: doc.format,
        variantClientId: doc.variant || undefined,
      });
      await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Regenerate failed.');
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  };

  const handleGenerateAll = async () => {
    if (!analysisId || !resolvedRunId) return;
    setBatchBusy(true);
    setActionError(null);
    try {
      await generateAllAnalysisDocuments(analysisId, resolvedRunId);
      await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Generate all failed.');
    } finally {
      setBatchBusy(false);
    }
  };

  const handleAuditExport = async () => {
    if (!analysisId || !resolvedRunId) return;
    setActionError(null);
    try {
      await downloadAuditExport(analysisId, resolvedRunId);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Audit export failed.');
    }
  };

  const empty = rows != null && rows.length === 0;
  const grouped = useMemo(() => {
    if (!rows) return [];
    return [...rows].sort((a, b) => {
      const orderA = KIND_ORDER[a.kind] ?? 99;
      const orderB = KIND_ORDER[b.kind] ?? 99;
      if (orderA !== orderB) return orderA - orderB;
      return a.title.localeCompare(b.title);
    });
  }, [rows]);

  return (
    <div className="space-y-3">
      {loadError ? (
        <InfoBanner variant="danger" showIcon>
          {loadError}
        </InfoBanner>
      ) : null}
      {actionError ? (
        <InfoBanner variant="danger" showIcon>
          {actionError}
        </InfoBanner>
      ) : null}

      <div className="bg-white border border-gray-200/80 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden">
        <div className="bg-gray-50/80 px-3 py-2.5 border-b border-gray-100 flex items-center justify-between gap-3">
          <span className="text-xs font-normal text-gray-700">Generated Documents</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refresh}
              className={workflowToolbarButtonClass}
              title="Refresh document list"
            >
              <RefreshCw size={13} className="mr-1.5" /> Refresh
            </button>
            {resolvedRunId ? (
              <button
                type="button"
                onClick={handleAuditExport}
                className={workflowToolbarButtonClass}
                title="Download a zip with every document, manifest, and audit trail for this run."
              >
                <Archive size={13} className="mr-1.5" /> Audit Export
              </button>
            ) : null}
            {resolvedRunId ? (
              <button
                type="button"
                onClick={handleGenerateAll}
                disabled={batchBusy || generationDisabled}
                className={workflowToolbarPrimaryButtonClass}
                title={
                  generationDisabled ? generationDisabledReason : 'Generate the standard document set for this run.'
                }
              >
                {batchBusy ? (
                  <>
                    <Loader size={13} className="mr-1.5 animate-spin" /> Generating…
                  </>
                ) : (
                  <>
                    <FilePlus size={13} className="mr-1.5" /> Generate All
                  </>
                )}
              </button>
            ) : null}
          </div>
        </div>

        {rows == null ? (
          <div className="flex items-center justify-center py-10 text-xs text-gray-500">
            <Loader size={16} className="mr-2 animate-spin" />
            Loading documents…
          </div>
        ) : empty ? (
          <div className="px-3 py-10 text-center">
            <FileText size={28} className="mx-auto text-gray-300 mb-2" />
            <p className="text-xs text-gray-500">
              No documents yet. Run 280G Results and then generate documents for this run.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/80 border-b border-gray-100">
                <tr>
                  <th scope="col" className={workflowTableHeadClass}>
                    Document
                  </th>
                  <th scope="col" className={workflowTableHeadClass}>
                    Kind
                  </th>
                  <th scope="col" className={workflowTableHeadClass}>
                    Format
                  </th>
                  <th scope="col" className={workflowTableHeadClass}>
                    Version
                  </th>
                  <th scope="col" className={workflowTableHeadClass}>
                    Status
                  </th>
                  <th scope="col" className={workflowTableHeadClass}>
                    Size
                  </th>
                  <th scope="col" className={workflowTableHeadClass}>
                    Generated
                  </th>
                  <th scope="col" className={workflowTableHeadClass}>
                    Last Error
                  </th>
                  <th scope="col" className={`${workflowTableHeadClass} text-right`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {grouped.map((doc) => {
                  const isBusy = busy.has(doc.id);
                  return (
                    <tr key={doc.id} className="hover:bg-gray-50/40">
                      <td className="px-3 py-3 text-xs font-normal text-gray-900">
                        <div className="truncate font-medium" title={doc.title}>
                          {doc.title}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-500">{doc.kind}</td>
                      <td className="px-3 py-3">
                        <Badge variant={doc.format === 'PDF' ? 'danger' : 'info'} size="table">
                          {doc.format}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-500 tabular-nums">v{doc.version}</td>
                      <td className="px-3 py-3">
                        <Badge variant={statusBadgeVariant(doc.status)} size="table">
                          {doc.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-500 tabular-nums">{formatBytes(doc.byteSize)}</td>
                      <td className="px-3 py-3 text-xs text-gray-500">{formatIsoDateTime(doc.generatedAt)}</td>
                      <td className="px-3 py-3 max-w-[11rem]">
                        <TruncatedErrorCell
                          message={doc.failureReason}
                          subject={doc.title}
                          maxWidthClass="max-w-full"
                          onView={
                            doc.failureReason
                              ? () => setErrorDetail({ title: doc.title, message: doc.failureReason })
                              : undefined
                          }
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {onPreview && doc.status === 'Ready' ? (
                            <button
                              type="button"
                              onClick={() => onPreview(doc)}
                              className={workflowTableIconButtonViewClass}
                              title="Preview"
                              aria-label={`Preview ${doc.title}`}
                            >
                              <Eye size={16} strokeWidth={2} aria-hidden />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => handleDownload(doc)}
                            disabled={doc.status !== 'Ready' || isBusy}
                            className={`${workflowTableIconButtonViewClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                            title={doc.status !== 'Ready' ? 'Document is not ready.' : 'Download'}
                            aria-label={`Download ${doc.title}`}
                          >
                            {isBusy ? (
                              <Loader size={16} className="animate-spin" aria-hidden />
                            ) : (
                              <Download size={16} strokeWidth={2} aria-hidden />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRegenerate(doc)}
                            disabled={isBusy || generationDisabled || doc.status === 'Generating'}
                            className={`${workflowTableIconButtonEditClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                            title={
                              generationDisabled
                                ? generationDisabledReason
                                : doc.status === 'Failed'
                                  ? 'Retry generation.'
                                  : 'Regenerate from the current run snapshot.'
                            }
                            aria-label={`Regenerate ${doc.title}`}
                          >
                            <RefreshCw size={16} strokeWidth={2} aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ErrorDetailModal
        open={!!errorDetail}
        title="Generation Error"
        subtitle={errorDetail?.title}
        message={errorDetail?.message}
        onClose={() => setErrorDetail(null)}
      />
    </div>
  );
}

const KIND_ORDER: Record<string, number> = {
  [DOCUMENT_KINDS.AnalysisSummary]: 0,
  [DOCUMENT_KINDS.DisclosureStatement]: 1,
  [DOCUMENT_KINDS.ShareholderConsent]: 2,
  [DOCUMENT_KINDS.WaiverAgreement]: 3,
};
